import { useHistory } from "react-router-dom";
import {
  formatArticleDate,
  formatShortDate,
} from "../../../domains/app/utils/renderDate";
import { useTranslation } from "react-i18next";
import { PlaylistComplianceNotes } from "../PlaylistComplianceNotes";
import { IconFileText } from "@tabler/icons-react";
import ShareV2Icon from "../../../assets/icons/share-v2.svg?react";
import BookMarkSimpleIcon from "../../../assets/icons/bookmark-simple.svg?react";
import BookMarkFillIcon from "../../../assets/icons/bookmark-fill.svg?react";
import JuisciMainAvatar from "../../../assets/icons/juisci-main-avatar.svg?react";
import { DesktopSaveButton } from "../../../components/save/DesktopSaveButton";
import VideoCard from "../../../components/cards/VideoCard";
import ShareSheet from "../../../components/SharedBySheet";
import { getInitials } from "../../../components/ProfilePicture";
import { Avatar } from "../../../components/ui/Avatar";
import { DesktopExploreRail } from "../../../components/discovery/DesktopExploreRail";
import { DesktopTopNav } from "../../../components/app/desktopTopNav/DesktopTopNav";
import { DesktopBreadcrumb } from "../../../components/app/desktopTopNav/DesktopBreadcrumb";
import {
  DesktopArticleRow,
  DesktopArticleRowSkeleton,
} from "../../../components/discovery/DesktopArticleRow";
import Skeleton from "../../../components/skeleton/Skeleton";
import { useActiveLang } from "../../../domains/app/hooks/useActiveLang";
import { useLoadedAudioId } from "../../../domains/app/contexts/audio.context";
import {
  getArticleTypeText,
  getArticleTypeVariant,
} from "../../../domains/app/utils/getArticleTypeText";
import { goBackInApp } from "../../../domains/app/utils/backNavigation";
import { authorLine } from "../../../domains/article/utils/authorLine";
import { articleReadingMinutes } from "../../../domains/article/utils/readingMinutes";
import { useClaimSharedPlaylist } from "../../../domains/playlist/hooks/useClaimSharedPlaylist";
import type { ApiPlaylistModel } from "../../../domains/playlist/hooks/useGetRecommendedPlaylistsQuery";
import { journalImpactFactor } from "../../../domains/journal/utils/journalImpactFactor";
import { usePlaylistPageView } from "../../../domains/playlist/hooks/usePlaylistPageView";
import { useSavePlaylist } from "../../../domains/playlist/hooks/useSavePlaylist";
import { useSharePlaylist } from "../../../domains/playlist/hooks/useSharePlaylist";
import { getRoomName } from "../../../domains/room/hooks/useRoomName";
import { sharedContentBackDestination } from "../../../domains/room/utils/roomContentPath";
import {
  PLAYLIST_ITEM_ATTRIBUTE,
  usePlaylistProgress,
} from "../usePlaylistProgress";
import { useCurrentUser } from "../../../domains/user/hooks/useCurrentUser";
import { useShareArticle } from "../../../domains/user/hooks/useShareArticle";
import { collectionOriginState } from "../../../domains/article/utils/collectionOrigin";
import { seedArticleDetailCache } from "../../../features/article/hooks/useArticleData";
import { useApiQuery, type Api } from "../../../services/api";
import { AnalyticsItemList } from "../../../tools/analytics/analytics.types";
import {
  getPlaylistRoomLogoUrl,
  PlaylistDescription,
} from "../PlaylistPageHeader";
import { z } from "zod";
import {
  displayToast,
  displayToastWithActions,
} from "../../../components/app/AppToast";
import { useLocationState } from "../../../domains/app/hooks/useLocationState";
import { apiClient } from "../../../services/api";
import { collectionBackdrop } from "../../../domains/playlist/collectionBackdrop";
import { CollectionCoverStack } from "./CollectionCoverStack";
import {
  OwnCollectionEmpty,
  OwnCollectionHeader,
  OwnCollectionSortableArticles,
  useRevalidateOwnCollection,
} from "./OwnCollection";
import { useCollectionScroll } from "./useCollectionScroll";
import {
  congressBreadcrumb,
  useCongressAttributionFromNavigation,
} from "../../../domains/congress/congressAttribution";

type CollectionArticle =
  Api["get"]["/webapp/public/content/batch-mixed"]["output"]["body"]["articles"][number];

function articleImpactFactor(article: CollectionArticle): number | null {
  return article.journal === null ? null : journalImpactFactor(article.journal);
}

/** The article's figure at card size, or `null` when it has none. */
function cardImageUrl(article: CollectionArticle): string | null {
  const cardImage = article.cardImage;
  return cardImage === null
    ? null
    : cardImage.sizes === null
      ? cardImage.url
      : cardImage.sizes.medium.file;
}

function articlePath(roomId: string | null, slug: string): string {
  return roomId === null ? `/post/${slug}` : `/room/post/${roomId}/${slug}`;
}

/**
 * When the collection last changed, for "Updated …": "today", "yesterday" and
 * "3 days ago" within the week, then the day ("Sep 19, 2026").
 */
function updatedWhen(value: string, locale: string): string {
  const startOfDay = (day: Date): number =>
    new Date(day.getFullYear(), day.getMonth(), day.getDate()).getTime();
  const days = Math.round(
    (startOfDay(new Date()) - startOfDay(new Date(value))) /
      (1000 * 60 * 60 * 24)
  );
  return days >= 0 && days < 7
    ? new Intl.RelativeTimeFormat(locale, { numeric: "auto" }).format(
        -days,
        "day"
      )
    : formatShortDate(value, locale);
}

/**
 * Who put the collection together (its room, its company, its author, or
 * Juisci) and when it last changed.
 */
function Curator({
  playlist,
}: {
  playlist: ApiPlaylistModel;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { activeLang } = useActiveLang();
  const room = playlist.room;
  const company = playlist.company;
  const user = playlist.user;
  const curator =
    room !== null
      ? {
          name: getRoomName(room, activeLang),
          logoUrl: getPlaylistRoomLogoUrl(room),
        }
      : company !== null
        ? {
            name: company.name,
            logoUrl: company.logo === null ? null : company.logo.url,
          }
        : user !== null
          ? { name: user.fullname, logoUrl: null }
          : null;
  return (
    <div className="flex items-center gap-2 pt-1 text-footnote">
      {curator === null ? (
        <JuisciMainAvatar className="size-6 shrink-0" />
      ) : curator.logoUrl === null ? (
        <Avatar
          variant="initials"
          initials={getInitials(curator.name)}
          size="sm"
          badge={null}
        />
      ) : (
        <Avatar
          variant="image"
          src={curator.logoUrl}
          alt=""
          imageFit="scale-down"
          size="sm"
          badge={null}
        />
      )}
      <span className="font-semibold text-ds-black">
        {t("By {{name}}", {
          name: curator === null ? "Juisci" : curator.name,
        })}
      </span>
      <span className="text-ds-slate-600">
        {`· ${t("Updated {{when}}", {
          when: updatedWhen(playlist.meta.lastModified, activeLang),
        })}`}
      </span>
    </div>
  );
}

/** One article of the list, with its own save flow (the playlist panel). */
function CollectionArticleRow({
  article,
  onOpen,
  onRemove,
}: {
  article: CollectionArticle;
  onOpen: () => void;
  /** The owner's ×; `null` for everyone else. */
  onRemove: (() => void) | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();
  const { activeLang } = useActiveLang();
  const { currentUser } = useCurrentUser();
  const { triggerShare } = useShareArticle(AnalyticsItemList.PLAYLIST_PAGE);
  const loadedAudioId = useLoadedAudioId();
  const journal = article.journal;
  return (
    <DesktopArticleRow
      layout="collection"
      title={article.title}
      author={authorLine(article.primary_author, article.authors)}
      description={null}
      date={formatArticleDate(article.publication_date, activeLang)}
      readingMinutes={articleReadingMinutes(article)}
      meta={
        article.article_type === null
          ? t("Article")
          : getArticleTypeText(article.article_type)
      }
      metaVariant={getArticleTypeVariant(article.article_type)}
      access={article.isOpenAccess === true ? "open" : "closed"}
      imageUrl={cardImageUrl(article)}
      isInPlayer={loadedAudioId === article.id}
      bookmark={
        <DesktopSaveButton
          article={article}
          analyticsListName={AnalyticsItemList.PLAYLIST_PAGE}
          size="row"
        />
      }
      onShare={
        currentUser === null
          ? null
          : () => {
              void triggerShare(article, currentUser);
            }
      }
      onRemove={onRemove}
      onClick={onOpen}
      source={
        journal === null
          ? null
          : {
              kind: "journal",
              name: journal.name,
              impactFactor: articleImpactFactor(article),
              onClick: () => {
                history.push(`/journal/${journal.uid}`);
              },
            }
      }
    />
  );
}

/** What opens a collection just created: its title in edit. */
const collectionLocationStateSchema = z.object({
  startsRenaming: z.boolean().default(false),
});

function DesktopPlaylistHeaderSkeleton(): React.JSX.Element {
  return (
    <div className="flex max-w-desktop-reading flex-col gap-3">
      <Skeleton className="rounded" width="80%" height={40} />
      <Skeleton className="rounded" width="100%" height={72} />
      <Skeleton className="rounded" width="30%" height={20} />
    </div>
  );
}

/**
 * A collection on a desktop, as a colleague opens it from a shared link
 * (Figma "UX/UI Desktop Version", "03 - My Collection / Collection Page",
 * frame "D3.collection.shared-link"): the cover blurred into a banner under
 * the top bar, the header with the cover stack, then the articles in the
 * curator's order beside the Explore rail. A reader's collection opened by
 * someone else says it is shared and is read only (no save); the others keep
 * their Save. Its owner edits it in place ("D3.library.collection").
 */
export function DesktopPlaylistPage({
  id,
  roomId,
}: {
  id: string;
  roomId: string | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();
  const { contentLang } = useActiveLang();
  const { currentUser, isAuthenticated } = useCurrentUser();
  const { scrollRef, isScrolled, onScroll, flush } = useCollectionScroll();
  useClaimSharedPlaylist(id);
  const { startsRenaming } = useLocationState(collectionLocationStateSchema);
  const revalidateCollection = useRevalidateOwnCollection(id);

  const playlistQuery = useApiQuery("/webapp/public/playlists/:playlistId", {
    param: { playlistId: id },
  });
  const roomQuery = useApiQuery(
    "/webapp/private/rooms/:id",
    roomId !== null ? { param: { id: roomId } } : false
  );
  const currentRoom = roomQuery.state === "success" ? roomQuery.body : null;
  const congressOrigin = useCongressAttributionFromNavigation();
  const playlist =
    playlistQuery.state === "success" ? playlistQuery.body : null;

  const articleIds = playlist === null ? [] : [...new Set(playlist.playlist)];
  const contentIds =
    playlist === null ? [] : [...new Set(playlist.playlistContent)];
  const batchQuery = useApiQuery(
    "/webapp/public/content/batch-mixed",
    articleIds.length === 0 && contentIds.length === 0
      ? false
      : {
          // An edit (remove, Undo, reorder, add) changes the ids: the list
          // keeps its rows while the new ones load, instead of skeletons.
          keepPreviousData: true,
          query: {
            articleIds: articleIds.join(","),
            contentIds: contentIds.join(","),
          },
        }
  );

  usePlaylistPageView({
    playlist,
    roomId,
    currentRoom,
    isRoomSettled: roomQuery.state !== "pending",
    userId: currentUser === null ? null : currentUser.id,
  });
  const { copyLink } = useSharePlaylist(
    roomId,
    playlist,
    AnalyticsItemList.PLAYLIST_PAGE
  );
  const { isSaved, toggleSave } = useSavePlaylist(
    playlist,
    AnalyticsItemList.PLAYLIST_PAGE
  );

  function handleBack(): void {
    playlistProgress.noteExit("back");
    if (isOwner) {
      goBackInApp(history, "/profile/playlist");
      return;
    }
    // Same destination rule as the mobile page and the article: a cold entry
    // has nothing behind it, and the room is only for someone who can open it.
    goBackInApp(
      history,
      sharedContentBackDestination(
        isAuthenticated
          ? { kind: "signed-in", roomId, hasRoom: currentRoom !== null }
          : { kind: "signed-out" }
      )
    );
  }

  // The collection's own trail, which an article opened from it continues.
  const trail =
    currentRoom === null
      ? [{ label: t("Explore"), to: "/discovery" }]
      : [
          { label: t("Rooms"), to: "/rooms" },
          {
            label: getRoomName(currentRoom, contentLang),
            to: `/room/discovery/${currentRoom.id}`,
          },
          // Opened from a congress space of this room.
          ...(congressOrigin !== null &&
          congressOrigin.roomId === currentRoom.id
            ? [congressBreadcrumb(congressOrigin)]
            : []),
        ];

  function openArticle(article: CollectionArticle): void {
    // Both callers draw a loaded collection: its header and its rows.
    if (playlist === null)
      throw new Error(
        "should not happen: an article opened before its collection loaded"
      );
    playlistProgress.noteItemOpened(article.id);
    seedArticleDetailCache(article, roomId);
    history.push(
      articlePath(roomId, article.slug),
      collectionOriginState({
        trail,
        collection: {
          label: playlist.title,
          path: history.location.pathname,
          congressAttribution: congressOrigin,
        },
      })
    );
  }

  // While an edit reloads the batch, it still holds the previous rows: only
  // the collection's current members are drawn and counted.
  const memberIds = new Set([...articleIds, ...contentIds]);
  const articles =
    batchQuery.state === "success"
      ? batchQuery.body.articles.filter((article) => memberIds.has(article.id))
      : [];
  const contents =
    batchQuery.state === "success"
      ? batchQuery.body.contents.filter((content) => memberIds.has(content.id))
      : [];
  const items = [...articles, ...contents];
  const playlistProgress = usePlaylistProgress(
    playlist !== null && batchQuery.state === "success" ? id : null
  );
  // The rows the list draws, articles and videos counted apart. Before the
  // list loads, the collection's article ids.
  const articleCount =
    batchQuery.state === "success" ? articles.length : articleIds.length;
  const videoCount = items.filter(
    (item) => item.content_format === "Video"
  ).length;
  const countLabel = [
    t("{{count}} articles", { count: articleCount }),
    ...(videoCount === 0 ? [] : [t("{{count}} videos", { count: videoCount })]),
  ].join(" · ");
  // The cover shows the collection's first three articles, in its own order
  // (the batch answers in its own).
  const coverArticles = articleIds
    .map((articleId) => articles.find((article) => article.id === articleId))
    .filter((article) => article !== undefined)
    .slice(0, 3);
  const coverCard = (article: CollectionArticle) => ({
    imageUrl: cardImageUrl(article),
    journalName: article.journal === null ? null : article.journal.name,
    impactFactor: articleImpactFactor(article),
    title: article.title,
    byline: [
      authorLine(article.primary_author, article.authors),
      t("{{count}} min read", { count: articleReadingMinutes(article) }),
    ]
      .filter((part) => part !== "")
      .join(" · "),
  });
  const coverImage = playlist === null ? undefined : playlist.images.at(0);

  // A direct MSL share is shared on only by its owner and its recipients.
  const sharedWithUserIds = playlist === null ? [] : playlist.sharedWithUserIds;
  const isSharedWithMe =
    currentUser !== null && sharedWithUserIds.includes(currentUser.id);
  const canShare =
    currentUser !== null &&
    playlist !== null &&
    (sharedWithUserIds.length === 0 ||
      isSharedWithMe ||
      currentUser.id === playlist.userId);
  // A collection a reader put together, opened by someone else from its link
  // (Figma "D3.collection.shared-link"): it says so, and it is read only. A
  // reader's collection is private; a KOL playlist also has a user, but it is
  // public and keeps its Save.
  const isSharedView =
    playlist !== null &&
    !playlist.isPublic &&
    playlist.user !== null &&
    (currentUser === null || currentUser.id !== playlist.user.id);
  // A playlist shared with you already lives in "My shared playlists":
  // saving it on top is redundant.
  const isOwner =
    currentUser !== null &&
    playlist !== null &&
    playlist.userId === currentUser.id;
  const canSave =
    currentUser !== null && !isOwner && !isSharedView && !isSharedWithMe;
  const isEmpty = articleIds.length === 0 && contentIds.length === 0;

  async function removeArticle(article: CollectionArticle): Promise<void> {
    if (playlist === null)
      throw new Error(
        "should not happen: an article removed before its collection loaded"
      );
    // Where it was, so Undo puts it back there.
    const position = playlist.playlist.indexOf(article.id);
    try {
      await apiClient.delete("/webapp/private/playlists/:playlistId/article", {
        param: { playlistId: id },
        json: { slug: article.slug },
      });
      await revalidateCollection();
      displayToastWithActions(t("Removed from this collection"), "info", [
        {
          label: t("Undo"),
          onClick: () => {
            void undoRemove(article.slug, position === -1 ? null : position);
          },
        },
      ]);
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  }

  async function undoRemove(
    slug: string,
    position: number | null
  ): Promise<void> {
    try {
      await apiClient.post("/webapp/private/playlists/:playlistId/article", {
        param: { playlistId: id },
        json: { slug, position },
      });
      await revalidateCollection();
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  }

  const header = (() => {
    switch (playlistQuery.state) {
      case "pending":
        return <DesktopPlaylistHeaderSkeleton />;
      case "error":
        return (
          <p className="m-0 text-desktop-body text-ds-slate-700">
            {t("This content is no longer available.")}
          </p>
        );
      case "success": {
        const loaded = playlistQuery.body;
        const journalCount =
          batchQuery.state === "success"
            ? new Set(
                articles.flatMap((article) =>
                  article.journal === null ? [] : [article.journal.id]
                )
              ).size
            : loaded.metrics.journals;
        const minutes = articles.reduce(
          (sum, article) => sum + articleReadingMinutes(article),
          0
        );
        const facts = [
          countLabel,
          // The stored count lags behind older collections: the loaded
          // articles say how many journals the list really holds.
          ...(journalCount === 0
            ? []
            : [t("{{count}} journals", { count: journalCount })]),
          ...(minutes === 0
            ? []
            : [t("{{count}} min read", { count: minutes })]),
        ];
        const factsLine = (
          <div className="flex items-center gap-1.5 text-footnote text-ds-slate-600">
            <IconFileText size={16} stroke={1.25} className="shrink-0" />
            {facts.join(" · ")}
          </div>
        );
        return (
          <div className="flex items-center justify-between gap-8">
            {isOwner ? (
              <div className="flex max-w-desktop-reading min-w-0 flex-1 flex-col gap-3">
                <OwnCollectionHeader
                  playlist={loaded}
                  articleCount={articleCount}
                  startsRenaming={startsRenaming}
                  curator={<Curator playlist={loaded} />}
                  facts={factsLine}
                  onShare={() => {
                    void copyLink();
                  }}
                />
              </div>
            ) : (
              <div className="flex max-w-desktop-reading min-w-0 flex-1 flex-col gap-3">
                {isSharedView ? (
                  <p className="m-0 text-footnote text-ds-slate-600">
                    {t("Shared collection")}
                  </p>
                ) : null}
                <h1 className="m-0 text-desktop-title font-semibold text-ds-black">
                  {loaded.title}
                </h1>
                {/* The editor's description, or the collection's summary when
                  it has none: the header says what the collection is about. */}
                {loaded.description !== null && loaded.description !== "" ? (
                  <div className="text-desktop-body text-ds-slate-700 [&_p]:m-0">
                    <PlaylistDescription
                      description={loaded.description}
                      label={loaded.descriptionLabel}
                    />
                  </div>
                ) : loaded.summary !== null && loaded.summary !== "" ? (
                  <p className="m-0 text-desktop-body whitespace-pre-line text-ds-slate-700">
                    {loaded.summary}
                  </p>
                ) : null}
                <Curator playlist={loaded} />
                {factsLine}
                {/* Listen and Ask JuisciGPT wait for a collection-wide
                  player and a collection context (Figma annotations). */}
                {canShare || canSave ? (
                  <div className="flex items-center gap-3 pt-2">
                    {canShare ? (
                      <button
                        type="button"
                        onClick={() => {
                          void copyLink();
                        }}
                        className="flex cursor-pointer items-center gap-1.5 rounded-lg border border-ds-slate-100 bg-ds-white py-1.5 pr-2.5 pl-3 text-footnote font-semibold text-ds-black hover:bg-ds-slate-100"
                      >
                        <ShareV2Icon className="size-4 shrink-0" />
                        {t("Share")}
                      </button>
                    ) : null}
                    {canSave ? (
                      <button
                        type="button"
                        aria-label={isSaved ? t("Saved") : t("Save")}
                        aria-pressed={isSaved}
                        onClick={toggleSave}
                        className="flex size-8.5 cursor-pointer items-center justify-center rounded-lg border border-ds-slate-100 bg-ds-white p-0 text-ds-black hover:bg-ds-slate-100"
                      >
                        {isSaved ? (
                          <BookMarkFillIcon className="size-5" />
                        ) : (
                          <BookMarkSimpleIcon className="size-5" />
                        )}
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            )}
            {coverArticles[0] === undefined ? null : (
              <div className="hidden xl:block">
                <CollectionCoverStack
                  front={coverCard(coverArticles[0])}
                  second={
                    coverArticles[1] === undefined
                      ? null
                      : coverCard(coverArticles[1])
                  }
                  third={
                    coverArticles[2] === undefined
                      ? null
                      : coverCard(coverArticles[2])
                  }
                />
              </div>
            )}
          </div>
        );
      }
      default:
        throw new Error(playlistQuery satisfies never);
    }
  })();

  const list = (() => {
    switch (batchQuery.state) {
      case "idle":
        // The collection is still loading (a failed one shows no list), or
        // it holds nothing.
        return playlist === null ? (
          <div className="flex flex-col divide-y divide-ds-slate-100">
            {Array.from({ length: 6 }).map((_, index) => (
              <DesktopArticleRowSkeleton key={index} />
            ))}
          </div>
        ) : null;
      case "pending":
        return (
          <div className="flex flex-col divide-y divide-ds-slate-100">
            {Array.from({ length: articleIds.length + contentIds.length }).map(
              (_, index) => (
                <DesktopArticleRowSkeleton key={index} />
              )
            )}
          </div>
        );
      case "error":
        return (
          <p className="m-0 py-4 text-footnote text-ds-slate-600">
            {t("Something went wrong. Please try again.")}
          </p>
        );
      case "success": {
        const articleRow = (item: CollectionArticle): React.JSX.Element => (
          <div key={item.id} {...{ [PLAYLIST_ITEM_ATTRIBUTE]: item.id }}>
            <CollectionArticleRow
              article={item}
              onOpen={() => {
                openArticle(item);
              }}
              onRemove={
                isOwner
                  ? () => {
                      void removeArticle(item);
                    }
                  : null
              }
            />
          </div>
        );
        return (
          <div
            ref={playlistProgress.listRef}
            className="flex flex-col divide-y divide-ds-slate-100 border-y border-ds-slate-100"
          >
            {isOwner && playlist !== null ? (
              <OwnCollectionSortableArticles
                playlistId={id}
                orderedIds={playlist.playlist}
                articles={articles}
                renderArticle={articleRow}
              />
            ) : (
              articles.map(articleRow)
            )}
            {contents.map((item) => {
              switch (item.content_format) {
                case "Video":
                  return (
                    <div
                      key={item.id}
                      className="py-4"
                      {...{ [PLAYLIST_ITEM_ATTRIBUTE]: item.id }}
                    >
                      <VideoCard
                        roomId={roomId}
                        video={item}
                        analyticsListName={AnalyticsItemList.PLAYLIST_PAGE}
                        recommendation={null}
                        onClick={() => {
                          playlistProgress.noteItemOpened(item.id);
                        }}
                        className={null}
                      />
                    </div>
                  );
                case "Nectar":
                case "Infographic":
                case "Audio":
                  return null;
                default:
                  throw new Error(item satisfies never);
              }
            })}
          </div>
        );
      }
      default:
        throw new Error(batchQuery satisfies never);
    }
  })();

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div
        className="relative min-h-0 flex-1 overflow-y-auto bg-ds-white"
        ref={scrollRef}
        onScroll={onScroll}
        // Any link followed from the page keeps a scroll that had not rested.
        onPointerDownCapture={flush}
        onKeyDownCapture={flush}
      >
        {/* The cover, dissolved into the banner the top bar floats over. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-117.5 overflow-hidden bg-ds-cream"
        >
          {playlist === null ? null : (
            // Wider than the banner, so the blur does not fade at its edges.
            // A collection with no image draws its programmatic backdrop.
            <div
              className="absolute -inset-16 bg-cover bg-center opacity-60 blur-collection-banner"
              style={{
                backgroundImage:
                  coverImage === undefined
                    ? collectionBackdrop(playlist.id)
                    : `url(${
                        coverImage.sizes === null
                          ? coverImage.url
                          : coverImage.sizes.large.file
                      })`,
              }}
            />
          )}
          <div className="absolute inset-0 bg-linear-to-b from-ds-white/0 via-ds-white/55 via-45% to-ds-white" />
        </div>

        <DesktopTopNav
          context={
            <DesktopBreadcrumb
              back={{ kind: "separate", onBack: handleBack }}
              trail={
                isOwner ? [{ label: t("Profile"), to: "/profile" }] : trail
              }
              current={
                isOwner
                  ? t("Collections")
                  : playlist === null
                    ? t("Collection")
                    : playlist.title
              }
            />
          }
          trailing={{ kind: "search" }}
          surface={isScrolled ? "default" : "overlay"}
          className={null}
        />

        <div className="relative px-10 pb-24">
          <div className="mx-auto flex max-w-desktop-block flex-col gap-10 pt-24">
            {header}
            {playlistQuery.state === "error" ? null : isOwner && isEmpty ? (
              <div className="max-w-desktop-column">
                <OwnCollectionEmpty playlistId={id} />
              </div>
            ) : (
              <div className="flex items-start gap-8">
                <div className="flex max-w-desktop-column min-w-0 flex-1 flex-col">
                  <h2 className="m-0 pb-2 text-heading-xs font-semibold text-ds-black">
                    {countLabel}
                  </h2>
                  {list}
                  {playlist === null ||
                  batchQuery.state === "pending" ? null : (
                    <PlaylistComplianceNotes
                      playlist={playlist}
                      className="pt-6"
                    />
                  )}
                </div>
                {currentUser === null ? null : (
                  <div className="hidden w-desktop-rail shrink-0 self-stretch border-l border-ds-slate-100 px-6 lg:block">
                    <div className="sticky top-20">
                      <DesktopExploreRail />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <ShareSheet type="playlist" />
    </div>
  );
}
